<template>
  <template v-if="state.matches('actionsList.mainActions')">
    <Status v-if="requests.length" :requests />
    <Title>
      {{ mainActionsTitle }}
      <template v-if="problemsPendingModeration?.length" #subtitle>
        {{ i18n(translations.pendingValidationSubtitle) }}
      </template>
    </Title>

    <RevInfoBlock
      v-if="!canCommunicateWithAgent"
      class="mb-16"
      :icon="IconWarning"
      :title="i18n(translations.cannotSendMessageBannerTitle)"
      variant="warning"
    >
      {{ i18n(translations.cannotSendMessageBannerContent) }}
    </RevInfoBlock>

    <div v-if="hasPrimaryActions" class="flex flex-wrap gap-12">
      <PrimaryActions :actions :send />
      <RevLink
        v-if="hasPrimaryActions && canCommunicateWithAgent"
        @click="send('OTHER_ACTIONS')"
      >
        {{ i18n(translations.otherActionsLink) }}
      </RevLink>
    </div>

    <SecondaryActions v-if="!hasPrimaryActions" :actions :send />
  </template>

  <template v-if="state.matches('actionsList.otherActions')">
    <Navigation withExit @back="send('BACK')" @exit="send('EXIT')" />
    <Title>{{ i18n(translations.otherActionsTitle) }}</Title>
    <SecondaryActions :actions :send />
  </template>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { RevInfoBlock } from '@ds/components/InfoBlock'
import { RevLink } from '@ds/components/Link'
import { IconWarning } from '@ds/icons/IconWarning'

import type { OrderlineRequest, Problem } from '~/api/seller-after-sales/types'

import type {
  Actions,
  MachineSend,
  MachineState,
} from '../../ActionBar.machine.type'
import Navigation from '../Navigation/Navigation.vue'
import Title from '../Title/Title.vue'

import translations from './ActionsList.translations'
import PrimaryActions from './components/PrimaryActions/PrimaryActions.vue'
import SecondaryActions from './components/SecondaryActions/SecondaryActions.vue'
import Status from './components/Status/Status.vue'

const { actions, requests, pendingProblems } = defineProps<{
  actions: Actions
  pendingProblems?: Problem[]
  send: MachineSend
  requests: OrderlineRequest[]
  state: MachineState
}>()

const i18n = useI18n()
const logger = useLogger()

const hasPrimaryActions = computed(() => {
  return (
    actions.isRefundAllowed ||
    actions.isShipBackToCustomerAllowed ||
    actions.isReportProblemAllowed
  )
})

const canCommunicateWithAgent = computed(() => {
  return actions.isSendMessageAllowed || actions.isReplyToAgentAllowed
})

const problemsPendingModeration = computed(
  () =>
    pendingProblems?.filter((problem) => problem.status === 'PENDING_AGENT') ||
    null,
)

const mainActionsTitle = computed(() => {
  if (
    !problemsPendingModeration.value ||
    !problemsPendingModeration.value.length
  )
    return i18n(translations.title)

  switch (problemsPendingModeration.value[0].type) {
    case 'OOW_ITEM':
      return i18n(translations.outOfWarrantyPendingValidationTitle)
    case 'ITEM_NOT_RECEIVED':
      return i18n(translations.itemNotReceivedPendingValidationTitle)
    case 'DAMAGED_PACKAGE':
      return i18n(translations.damagedPackagePendingValidationTitle)
    default:
      logger.error(
        `[ASP Seller Action bar] Problem type '${problemsPendingModeration.value[0]?.type}' is not supported for moderation.`,
        { owners: ['bot-squad-seller-care-front'] },
      )

      return i18n(translations.title)
  }
})
</script>
